import { type FC } from "react";
import { type LinearCstic, type LinearSubstep } from "../../Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "../../Linear/components/SubstepFrame";
import LinearTile from "../../Linear/components/LinearTile";
import { defaultMemoize } from "reselect";
import { Col, Row } from "../../../common/components/Layout";
import { updateCsticValuesOld } from "../../Linear/redux/actions";
import { getCarriageKmatCsticMap } from "../../Linear/redux/selectors";
import { SubstepType } from "../../Linear/dtos";
import { Card } from "../../Linear/components/Card";
import { useTranslation, type WithTranslation } from "react-i18next";
import { CARRIAGE_LOCATING_CSTIC_NAME } from "../carriageConfiguratorConstants";
import LanguageAwareExternalContent from "../../Linear/components/LanguageAwareExternalContent";
import { CURRENT_LANGUAGE } from "../../../utils/constants";
import { formatCsticsToSet } from "../../Linear/utils";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const getLocatingFaceImageUrl = (cAbove: boolean) => {
  let fileName;
  if (CURRENT_LANGUAGE.toLowerCase() === "de") {
    if (cAbove) {
      fileName = "00196649_mei_in_de_de.svg";
    } else {
      fileName = "0019664B_mei_in_de_de.svg";
    }
  } else {
    if (cAbove) {
      fileName = "00196B98_mei_in_en_gb.svg";
    } else {
      fileName = "00196BA8_mei_in_en_gb.svg";
    }
  }
  return `https://cdn.schaeffler-ecommerce.com/cdn/${fileName}`;
};

const CarriageLocatingFaceSubstep: FC<Props> = ({ def, ...props }) => {
  const cstics = useSelector(getCarriageKmatCsticMap);
  const { t } = useTranslation();
  const carriageLocatingFaceCstic = cstics[CARRIAGE_LOCATING_CSTIC_NAME];
  if (!carriageLocatingFaceCstic) {
    return null;
  }

  const tile = getTileData(cstics, t);

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          <Col size={4}>
            <SingleGuidewayTile {...tile} />
          </Col>
        </Row>
      </div>

      {def.footerInfo.length > 0 && (
        <div className="mb-3">
          {def.footerInfo.map((info) => (
            <Alert key={info} className="mb-2" severity="info">
              <LanguageAwareExternalContent content={info} loadedClassname={"scha__lc__external-label"} />
            </Alert>
          ))}
        </div>
      )}
    </SubstepFrame>
  );
};
export default CarriageLocatingFaceSubstep;

interface TileProps {
  label: string;
  index: number;
  carriageCstic: LinearCstic | undefined;
}

const SingleGuidewayTile: FC<TileProps> = (props) => {
  const { label, carriageCstic } = props;

  const dispatch = useDispatch();
  if (!carriageCstic) {
    return null;
  }

  const cAValue = carriageCstic.values[0];
  const cBValue = carriageCstic.values[1];

  const currentCValue = carriageCstic.value;

  const setCA = () => dispatch(updateCsticValuesOld({ [carriageCstic.name]: cAValue.value }, SubstepType.LocatingFace));
  const setCB = () => dispatch(updateCsticValuesOld({ [carriageCstic.name]: cBValue.value }, SubstepType.LocatingFace));

  if (!cAValue || !cBValue) {
    return null;
  }

  const cAbove = currentCValue === cAValue.value;

  const debugLanguage = isDebugLanguageEnabled();

  return (
    <Card disableHover={true}>
      <div className={"p-30"}>
        <Row>
          <Col size={12}>
            <LinearTile
              index={0}
              title={
                debugLanguage ? (
                  formatCsticsToSet({ [carriageCstic.name]: cAValue.value })
                ) : (
                  <LanguageAwareExternalContent content={cAValue.label} loadedClassname={"scha__lc__external-label"} />
                )
              }
              isStandard={cAValue.standard}
              isPicked={cAbove}
              forceStandardSpace={true}
              onClickHandler={setCA}
            />
          </Col>
        </Row>

        <div className={"my-3"}>
          <img className={"w-100"} src={getLocatingFaceImageUrl(cAbove)} />
        </div>

        <Row>
          <Col size={12}>
            <LinearTile
              index={1}
              title={
                debugLanguage ? (
                  formatCsticsToSet({ [carriageCstic.name]: cBValue.value })
                ) : (
                  <LanguageAwareExternalContent content={cBValue.label} loadedClassname={"scha__lc__external-label"} />
                )
              }
              isStandard={cBValue.standard}
              isPicked={!cAbove}
              forceStandardSpace={true}
              onClickHandler={setCB}
            />
          </Col>
        </Row>
      </div>
    </Card>
  );
};

const getTileData = defaultMemoize((cstics: Dict<LinearCstic>, t: WithTranslation["t"]): TileProps => {
  const indexLabel = t("locating.face.guideway.set.label", { index: 1 });
  const carriageCstic = cstics[CARRIAGE_LOCATING_CSTIC_NAME];

  return {
    carriageCstic,
    index: 1,
    label: indexLabel,
  };
});
